import { useId, useState } from "react";
import { useSettings, type ConnectionView, type Me } from "@helm/core";
import { networkStatus } from "@helm/ui";
import { settingsSync } from "../session";
import { LockoutPanel } from "../topbar/LockoutPanel";
import { useAccountView } from "../viewmodel/account";
import { Journal } from "./Journal";
import { RithmicPanel } from "../settings/RithmicPanel";
import { lazyComponent } from "../components/lazy";
import { SignOutButton } from "../auth/SignOutButton";

// Rarely opened sections load on first open (audit S-12). Rithmic logins and the lockout stay in
// the entry chunk: they decide whether HELM can trade at all.
const DisciplineSettings = lazyComponent(
  () => import("../coach/DisciplineSettings").then((m) => m.DisciplineSettings),
  "discipline settings",
);
const SettingsForm = lazyComponent(
  () => import("../settings/SettingsForm").then((m) => m.SettingsForm),
  "preferences",
);
const RiskSettingsForm = lazyComponent(
  () => import("../risk/RiskSettingsForm").then((m) => m.RiskSettingsForm),
  "risk settings",
);
const DevicesPanel = lazyComponent(
  () => import("../settings/DevicesPanel").then((m) => m.DevicesPanel),
  "devices",
);
const SecurityPanel = lazyComponent(
  () => import("../settings/SecurityPanel").then((m) => m.SecurityPanel),
  "security",
);
const CopyTraderPanel = lazyComponent(
  () => import("../copy/CopyTraderPanel").then((m) => m.CopyTraderPanel),
  "copy trader",
);

/**
 * Mobile Account (Tradesea): accounts, risk settings, personal lockout, journal calendar,
 * preferences, devices, passkeys, connection details, sign out. Sections are disclosure widgets so the tab stays
 * short on a phone.
 */
export function Account({ me, connection }: { me: Me; connection: ConnectionView }): React.JSX.Element {
  const view = useAccountView();
  const privacy = useSettings((s) => s.settings.privacy);
  const net = networkStatus(connection);
  return (
    <div className="list m-account-tab">
      <Section title="Accounts" defaultOpen>
        <div className="list-row">
          <span>{view.active ? (privacy ? "***" : view.active.label) : "No Rithmic account connected"}</span>
        </div>
      </Section>
      <Section title="Rithmic logins">
        <RithmicPanel />
      </Section>
      <Section title="Copy trader">
        <CopyTraderPanel compact />
      </Section>
      <Section title="Account information">
        <RiskSettingsForm />
      </Section>
      <Section title="Personal Lockout">
        <LockoutPanel lockedUntil={view.lockedUntil} />
      </Section>
      <Section title="Journal">
        <Journal />
      </Section>
      <Section title="Preferences">
        <SettingsForm sync={settingsSync} />
      </Section>
      <Section title="Discipline">
        <DisciplineSettings />
      </Section>
      <Section title="Devices">
        <DevicesPanel />
      </Section>
      <Section title="Security">
        <SecurityPanel />
      </Section>
      <div className="list-row">
        <span>Signed in as</span>
        <span className="muted">{me.email}</span>
      </div>
      <div className="list-row">
        <span>Network</span>
        <span className="muted" title={net.detail}>
          {net.label}
          {connection.gwRttMs === null ? "" : ` · ${String(connection.gwRttMs)} ms`}
        </span>
      </div>
      <SignOutButton className="btn btn-block" />
    </div>
  );
}

function Section({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="m-section">
      <h2 className="m-section-h">
        <button
          type="button"
          className="m-section-btn"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => {
            setOpen((o) => !o);
          }}
        >
          <span>{title}</span>
          <span aria-hidden="true" className={open ? "chev open" : "chev"}>
            ›
          </span>
        </button>
      </h2>
      <div id={id} hidden={!open} className="m-section-body">
        {open && children}
      </div>
    </section>
  );
}
